import { IUserPermission } from '@/types/user'
import { PageAllTypes } from '@/types/utility.types'
import {
  createContext,
  useContext,
  ReactNode,
  useState,
  SetStateAction,
  Dispatch,
  useCallback,
  Suspense,
} from 'react'

type PermissionsType = IUserPermission[] | null
type ModulePermissionsType = Record<PageAllTypes, boolean>

const ALL_GRANTED: ModulePermissionsType = {
  list: true,
  create: true,
  retrieve: true,
  update: true,
  destroy: true,
  options: true,
  force_update: true,
  approve: true,
  reject: true,
  update_status: true,
}

interface IPermissionsContext {
  permissions: PermissionsType
  setPermissions: Dispatch<SetStateAction<PermissionsType>>
  getModulePermissions: (module: string) => ModulePermissionsType | null
}

const PermissionsContext = createContext<IPermissionsContext | null>(null)

export const PermissionsProvider = ({ children }: { children: ReactNode }) => {
  const [permissions, setPermissions] = useState<PermissionsType>(null)

  const getModulePermissions = useCallback(
    (module: string) => {
      const permission = permissions?.find(
        (p) => p.module.module_key === module
      )

      if (permission?.is_granted) return ALL_GRANTED

      const modulePermissions = permission?.actions.reduce(
        (acc, curr) => ({
          ...acc,
          [curr.action_key]: curr.action_value,
        }),
        {}
      )

      return (modulePermissions as ModulePermissionsType) ?? null
    },
    [permissions]
  )

  return (
    <Suspense>
      <PermissionsContext.Provider
        value={{
          permissions,
          setPermissions,
          getModulePermissions,
        }}
      >
        {children}
      </PermissionsContext.Provider>
    </Suspense>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export const usePermissions = (): IPermissionsContext => {
  const context = useContext(PermissionsContext)
  if (!context) {
    throw new Error(
      `'usePermissions' must be used within an PermissionsProvider`
    )
  }
  return context
}
